import type { TickCtx, GameState } from '../tickTypes'
import { Dec } from '../../shared/decimal'

// updateSmall section: holy tetrahedrons/octahedrons, holy fire, holy
// dodecahedrons, planets (script.js:1858-1984, state statements only).
export function holyPlanets(game: GameState, _ctx: TickCtx): void {
  // script.js:1858-1873 — holy tetrahedrons
  if (game.unlocks >= 24) {
    // :1859-1866 — compute the UNFLOORED value first (the oracle feeds this
    // intermediate to DOM lines :1869-1870 before flooring at :1872; we keep
    // compute-then-floor as two steps so the final state matches exactly).
    game.holyTetrahedronsToGet = game.gold.add(1).log10().div(1e30).pow(0.05)
    if (game.unlocks >= 26) {
      // :1861-1863 — terse local softcap/hardcap block
      let holyFireUpgrade2Effect = new Dec(3).pow(game.holyFireUpgradesBought[1].pow(0.6))
      if (holyFireUpgrade2Effect.gt('1e2000')) holyFireUpgrade2Effect = holyFireUpgrade2Effect.mul('1e2000').pow(0.5)
      if (holyFireUpgrade2Effect.gt('1e10000')) holyFireUpgrade2Effect = new Dec('1e10000')
      game.holyTetrahedronsToGet = game.holyTetrahedronsToGet.mul(holyFireUpgrade2Effect)
    }
    if (game.voidMagicUpgradesBought[10] == true) game.holyTetrahedronsToGet = game.holyTetrahedronsToGet.mul(game.planets.pow(1.5).add(1))
    // :1872 — final floor (DOM-only lines :1867-1871 skipped)
    game.holyTetrahedronsToGet = game.holyTetrahedronsToGet.floor()
  }

  // script.js:1874-1886 — holy octahedrons
  if (game.unlocks >= 25) {
    // :1875-1879 — unfloored compute (DOM reads it, then :1885 floors)
    game.holyOctahedronsToGet = game.gold.add(1).log10().div(1e60).pow(0.02)
    if (game.unlocks >= 26) game.holyOctahedronsToGet = game.holyOctahedronsToGet.mul(new Dec(2).pow(game.holyFireUpgradesBought[4].pow(0.6)))
    if (game.voidMagicUpgradesBought[0]) game.holyOctahedronsToGet = game.holyOctahedronsToGet.mul(10)
    if (game.voidMagicUpgradesBought[10] == true) game.holyOctahedronsToGet = game.holyOctahedronsToGet.mul(game.planets.pow(1.5).add(1))
    if (game.voidMagicUpgradesBought[9] == true) game.holyOctahedronsToGet = game.holyOctahedronsToGet.pow(1.25)
    // :1885 — final floor
    game.holyOctahedronsToGet = game.holyOctahedronsToGet.floor()
  }

  // script.js:1887-1939 — holy fire (state lines :1888-1896 only; the local
  // holyFireUpgrade2Effect/holyFireUpgrade6Effect blocks at :1906-1938 feed
  // DOM text exclusively and are skipped)
  if (game.unlocks >= 26) {
    game.holyFirePerSecond = game.blueFire.add(1).log10().div(100)
    game.holyFirePerSecond = game.holyFirePerSecond.mul(new Dec(2).pow(game.holyFireUpgradesBought[0].pow(0.6)))
    if (game.holyTetrahedronUpgradesBought[12]) game.holyFirePerSecond = game.holyFirePerSecond.mul(100)
    if (game.holyOctahedronUpgradesBought[7]) game.holyFirePerSecond = game.holyFirePerSecond.mul(game.holyOctahedrons.add(1))
    if (game.holyDodecahedronUpgradesBought[5]) game.holyFirePerSecond = game.holyFirePerSecond.mul(game.holyDodecahedrons.add(1))
    if (game.dragonStage >= 8) game.holyFirePerSecond = game.holyFirePerSecond.mul(1e10)
    // :1894 — reads game.planetEffect from the PREVIOUS tick (planets block
    // below runs after this one in the same pass)
    if (game.unlocks >= 29) game.holyFirePerSecond = game.holyFirePerSecond.pow(game.planetEffect)
    if (game.unlocks >= 31) game.holyFirePerSecond = game.holyFirePerSecond.pow(game.plagueUpgradesBought[3].pow(0.5).div(10).add(1))
    if (game.unlocks >= 36) game.holyFirePerSecond = game.holyFirePerSecond.pow(new Dec(1.1).pow(game.finalityEssenceUpgradesBought[1].pow(0.5)))
  }

  // script.js:1940-1955 — holy dodecahedrons
  if (game.unlocks >= 28) {
    // :1941-1948 — unfloored compute (DOM reads it, then :1954 floors)
    game.holyDodecahedronsToGet = game.gold.add(1).log10().div(1e140).pow(0.02)
    if (game.voidMagicUpgradesBought[7] == true) {
      // :1943-1945 — terse local softcap/hardcap block
      let holyFireUpgrade6Effect = new Dec(2).pow(game.holyFireUpgradesBought[5].pow(0.7))
      if (holyFireUpgrade6Effect.gt('1e2000')) holyFireUpgrade6Effect = holyFireUpgrade6Effect.mul('1e2000').pow(0.5)
      if (holyFireUpgrade6Effect.gt('1e10000')) holyFireUpgrade6Effect = new Dec('1e10000')
      game.holyDodecahedronsToGet = game.holyDodecahedronsToGet.mul(holyFireUpgrade6Effect)
    }
    if (game.voidMagicUpgradesBought[10] == true) game.holyDodecahedronsToGet = game.holyDodecahedronsToGet.mul(game.planets.pow(1.5).add(1))
    // :1954 — final floor
    game.holyDodecahedronsToGet = game.holyDodecahedronsToGet.floor()
  }

  // script.js:1956-1984 — planets / superclusters
  if (game.unlocks >= 29) {
    game.planetEffect = game.planets.pow(0.8).div(5).add(1)
    // :1958-1969 — supercluster factor (three-way branch; the nuclearPasta
    // arm includes the Decimal.min(0.02*pasta^0.6+1, 1.3) cap factor)
    if (game.unlocks >= 35 && (game.nuclearPastaState == 4 || game.nuclearPastaUpgradesBought[3])) {
      game.planetEffect = game.planetEffect.mul(game.superclusters.pow(0.7).div(8).add(1).mul(Dec.min(new Dec(0.02).mul(game.nuclearPasta.pow(0.6)).add(1), 1.3)))
    }
    else if (game.voidMagicUpgradesBought[14] == true) {
      game.planetEffect = game.planetEffect.mul(game.superclusters.pow(0.7).div(8).add(1))
    }
    else {
      game.planetEffect = game.planetEffect.mul(game.superclusters.pow(0.7).div(10).add(1))
    }
    if (game.voidMagicUpgradesBought[13] == true) game.planetEffect = game.planetEffect.mul(1.2)
    if (game.unlocks >= 31) game.planetEffect = game.planetEffect.mul(game.plagueUpgradesBought[1].add(1).log10().div(4).add(1))
    if (game.unlocks >= 32) game.planetEffect = game.planetEffect.add(game.oganessonUpgradesBought[2] * 2)
    // :1973-1976 — softcap; NOTE the oracle multiplies by 600 (not divides)
    // before the sqrt: pow(0.5) of effect*600
    if (game.planetEffect.gte(600)) {
      game.planetEffect = game.planetEffect.mul(600).pow(0.5)
    }
  }
}
